import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'stores/store';
import {
  RequestStatus,
  REQUEST_STATUSES,
} from 'stores/common';
import {
  getMe,
  postSignInProvider,
  postSignOut,
} from './authClient';

export interface User {
  id: string,
  firstName: string,
  lastName: string,
  address: string,
  rpps: string,
  r_social: string,
  n_struct: string,
}

export enum AUTH_STATUSES {
  NOT_CHECKED,
  PENDING,
  LOGGED,
  NOT_LOGGED,
  LOGGED_OUT,
  NEW_PASSWORD_REQUIRED,
}

export interface AuthState {
  user?: User,
  authStatus: AUTH_STATUSES,
  authRequestStatus?: RequestStatus,
  signinError?: String,
  signinRequestStatus?: RequestStatus,
}

const initialState: AuthState = {
  authStatus: AUTH_STATUSES.NOT_CHECKED,
};

interface ISignInProviderAsync {
  accessToken: string;
  clientId: string;
  provider: string;
}
export const signInProviderAsync = createAsyncThunk(
  'auth/signin_provider',
  ({ provider, clientId, accessToken }: ISignInProviderAsync) => postSignInProvider(
    provider,
    clientId,
    accessToken,
  ),
);

export const checkAuthAsync = createAsyncThunk('auth/check', () => getMe());
export const expireSessionAsync = createAsyncThunk('auth/expire-session', () => postSignOut());
export const signoutAsync = createAsyncThunk('auth/signout', () => postSignOut());

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthStatus: (state, action: PayloadAction<AUTH_STATUSES>) => {
      state.authStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInProviderAsync.pending, (state) => {
        state.authStatus = AUTH_STATUSES.PENDING;
        state.signinRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(signInProviderAsync.fulfilled, (state, { payload }) => {
        state.authStatus = AUTH_STATUSES.LOGGED;
        state.signinRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.user = payload.user;
      })
      .addCase(signInProviderAsync.rejected, (state) => {
        state.authStatus = AUTH_STATUSES.NOT_LOGGED;
        state.signinRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(checkAuthAsync.pending, (state) => {
        state.authRequestStatus = REQUEST_STATUSES.PENDING;
        state.authStatus = AUTH_STATUSES.PENDING;
      })
      .addCase(checkAuthAsync.fulfilled, (state, { payload }) => {
        state.authRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.authStatus = AUTH_STATUSES.LOGGED;
        state.user = payload;
      })
      .addCase(checkAuthAsync.rejected, (state) => {
        state.authRequestStatus = REQUEST_STATUSES.ERROR;
        state.authStatus = AUTH_STATUSES.NOT_LOGGED;
      })
      .addCase(signoutAsync.fulfilled, (state) => {
        state.authStatus = AUTH_STATUSES.LOGGED_OUT;
        delete state.user;
      });
  },
});

export const { setAuthStatus } = authSlice.actions;
export const getUser = (state: RootState) => state.auth.user;
export const getAuthStatus = (state: RootState) => state.auth.authStatus;
export const getAuthRequestStatus = (state: RootState) => state.auth.authRequestStatus;

export default authSlice.reducer;
